<template>
  <div>
    <Header> New Request Form </Header>
    <div class="tiled-bar-image"></div>
    <Main>
      <div class="flex justify-center p-2">
        <Button
          color="gray"
          size="md"
          @click="toStudentList()">
          Back to Student List
        </Button>
      </div>
      <div class="flex flex-col justify-center p-4"
        v-if="showNewStudentPdfUpload">
        <h5 class="m-2">
          PDF Data Upload
        </h5>
        <NewStudentPdfUpload ref="pdfUpload"
          @populateForm="populateForm"
        />
      </div>
      <RequestForm ref="requestForm"
        :defaultValues="fromRequestFormValues"
        :enableSchoolYearSelection="true"
        :allowDistrictSelection="true"
        :schoolYearSelectionOptions="schoolYearSelectionOptions"
        :studentDistrictSelectionOptions="studentDistrictSelectionOptions"
        @selectedStudentDistrict="onSelectedStudentDistrict"
        @submit="onSubmit"
        @editSuggestedStudent="onEditSuggestedStudent"
      />
    </Main>
  </div>
</template>

<style scoped>
.tiled-bar-image {
  width: 100%;
  height: 25px;
  background-image: url("@/assets/Bar.png");
  background-repeat: repeat-x;
}
</style>

<script>
import Header from '../components/Header.vue'
import Main from '../components/Main.vue'
import Button from '../components/Button.vue'

import NewStudentPdfUpload from '../components/NewStudentPdfUpload.vue'
import RequestForm from '../pages/RequestForm.vue'

import newRequestFormService from '../services/pages/newRequestForm.js'

import user from '../services/user/user.js'
import view from '../services/user/view.js'
import notifications from '../services/notifications.js'

import districtUtil from '../services/districts.js'
import partitionUtil from '../services/partitions.js'

import settings from '../services/settings.js'
import uiData from '../services/uiData.js'
import events from '../services/events.js'
import cloneDeep from 'clone-deep'

export default {
  components: {
    Header,
    Main,
    Button,
    NewStudentPdfUpload,
    RequestForm,
  },
  props: {},
  data() {
    return {
      user: undefined,
      view: undefined,

      fromRequestFormValues: undefined,
      submissionResponse: undefined,

      schoolYearSelectionOptions: [],
      studentDistrictSelectionOptions: [],

      pdfDistrictSelected: false,
    }
  },
  eventCallbackIds: {},
  methods: {
    async calculateLeadTime(districtList) {

      await Promise.all([ districtUtil.getDistrictFromServer(), partitionUtil.getPartitionFromServer() ])

      let leadTimes = [1]
      for (const dist of districtList) {
        let districtObj = await districtUtil.getDistrictByCode(dist)
        let result = await partitionUtil.getPartition(districtObj['partition_id'])
        if (result['ui_data']['request_lead_time']) {
          leadTimes.push(result['ui_data']['request_lead_time'])
        }
      }
      return Math.max(...leadTimes)
    },
    async serveUiData() {
      this.$refs.requestForm.requireBoosterSeat(false)

      let districtList = this.view.getDistrict()
      if (districtList.length > 0){
        await this.calculateLeadTime(districtList).then(result => {
          this.$refs.requestForm.setLeadTime(result)
        })
      }
      uiData.using('partition', (uiDataObj) => {
        for (const partitionId of this.view.getPartition()) {
          if (uiDataObj[partitionId] === undefined) {
            continue
          }
          if ('conditions' in uiDataObj[partitionId]) {
            let conditions = uiDataObj[partitionId].conditions
            if ('require_booster_seat' in conditions) {
              for (const form of conditions['require_booster_seat']['forms']) {
                if (form === 'new_request') {
                  let gradeMin = conditions['require_booster_seat']['grade_min']
                  let gradeMax = conditions['require_booster_seat']['grade_max']
                  let warningMessage = conditions['require_booster_seat']['warning_message']
                  this.$refs.requestForm.requireBoosterSeat(true, gradeMin, gradeMax, warningMessage)
                }
              }
            }
          }
          if ('request_lead_time' in uiDataObj[partitionId] && districtList.length == 0) {
            let leadTime = uiDataObj[partitionId]['request_lead_time']
            this.$refs.requestForm.setLeadTime(leadTime)
          }
        }
      })
    },
    async updateRequestFormOptions() {
      let [districtOptions, schoolYearOptions] = await newRequestFormService.getNewRequestFormOptionsFromCapability(this.user)
      this.studentDistrictSelectionOptions = districtOptions
      this.schoolYearSelectionOptions = schoolYearOptions
    },
    onSelectedStudentDistrict(newDistrictCode) {
      if (this.user === undefined || !newDistrictCode) {
        this.pdfDistrictSelected = false
        return
      }

      this.pdfDistrictSelected = this.user.can('submit_new_student_pdf', { district: newDistrictCode })
    },
    async onSubmit(resolve, reject, clonedFormValues, isSibling) {
      try {
        let response = await newRequestFormService.postNewStudentForm(clonedFormValues)
        if (response.status !== 201) {
          throw new Error(response.statusText)
        }
        resolve(response)

        if (isSibling) {
          // settings.requestForm.pullInit = true;
          this.$refs.requestForm.clearStudentInfo()
          this.$refs.requestForm.updateBuildingSelection()
          notifications.pushInfo('All validation complete, form submitted, continue to fill out the next form.')
          window.scrollTo(0, 0)
        }
        else {
          this.$router.push({
            name: 'StudentList',
          })
          notifications.pushInfo('All validation complete, form submitted.')
        }
      }
      catch (err) {
        reject(err)
        console.log(err)
      }
    },
    toStudentList() {
      settings.requestForm.pullInit = false
      this.$router.push({
        name: 'StudentList',
      })
    },
    onEditSuggestedStudent(dbId) {
      settings.requestForm.dbId = dbId
      this.$router.push({
        name: 'ChangeRequestForm'
      })
    },
    async setRequestFormValues(dbId) {
      window.scrollTo(0, 0);
      try {
        let tempObjRef = await newRequestFormService.setRequestFormValues(dbId)
        delete tempObjRef['firstName']
        delete tempObjRef['middleName']
        delete tempObjRef['lastName']
        delete tempObjRef['dateOfBirth']
        delete tempObjRef['gender']
        delete tempObjRef['studentId']
        delete tempObjRef['marssCode']
        delete tempObjRef['grade']
        if (tempObjRef.schoolYear === undefined) {
          this.view = await view.get()
          let schoolYearCodeList = this.view.getSchoolYear()
          tempObjRef.schoolYear = schoolYearCodeList[0]
        }

        this.fromRequestFormValues = tempObjRef
      }
      catch {
        this.$router.push({
          name: 'NewRequestForm',
        })
      }
    },
    async populateForm(formValues) {
      this.$refs.requestForm.resetComponentsUpdated()
      this.$refs.requestForm.setDisableDefaultValueUpdates(false)
      let tempObjRef = cloneDeep(formValues)

      if (!tempObjRef.schoolYear) {
        this.view = await view.get()
        let schoolYearCodeList = this.view.getSchoolYear()
        tempObjRef.schoolYear = schoolYearCodeList[0]
      }

      if (!tempObjRef.district) {
        delete tempObjRef.district
      }
      if (!tempObjRef.infoRequestingDistrict) {
        delete tempObjRef.infoRequestingDistrict
      }
      if (!tempObjRef.infoResidentDistrict) {
        delete tempObjRef.infoResidentDistrict
      }

      this.fromRequestFormValues = tempObjRef
    },
    checkUserCanViewPage() {
      if (!this.user.can('view_new_request_form')) {
        this.$router.push({
          name: 'StudentList'
        })
        return true
      }
    },
  },
  computed: {
    showNewStudentPdfUpload() {
      if (this.user === undefined) return false
      if (this.view === undefined) return false

      return this.pdfDistrictSelected || this.user.can('submit_new_student_pdf', { district: this.view.getDistrict() })
    }
  },
  async mounted() {
    // this.$refs.pdfUpload.setService(newStudentPdfService)

    this.user = await user.get()
    this.view = await view.get()
    if (this.checkUserCanViewPage()) {
      return
    }

    if (settings.requestForm.pullInit) {
      this.dbId = parseInt(settings.requestForm.dbId)
      settings.requestForm.pullInit = false
      this.setRequestFormValues(this.dbId)
    }
    else {
      let schoolYearCodes = this.view.getSchoolYear()
      let districtCode = undefined
      if (!this.user.can('select_district')) {
        districtCode = this.user.getConfig('district_code')
      }
      this.fromRequestFormValues = {
        schoolYear: schoolYearCodes[0],
        district: districtCode,
      }
    }
    this.updateRequestFormOptions()
    this.serveUiData()

    window.scrollTo(0,0)
  },
  created() {
    this.$options.eventCallbackIds.viewUpdated = events.register(
      'viewUpdated',
      async () => {
        this.view = await view.get()
        this.updateRequestFormOptions()
      }
    )

    this.$options.eventCallbackIds.uiDataUpdated = events.register(
      'uiDataUpdated',
      () => {
        this.serveUiData()
      }
    )
  },
  //beforeUnmount() {
  // eslint-disable-next-line
  beforeDestroy() {
    events.remove(
      'viewUpdated',
      this.$options.eventCallbackIds.viewUpdated
    )
    events.remove(
      'uiDataUpdated',
      this.$options.eventCallbackIds.uiDataUpdated
    )
  },
}
</script>
